import { create } from "zustand";
import { useEffect } from "react";
import axios from "../lib/axios";

interface JwtPayload {
  exp?: number;
}

let authCheckPromise: Promise<void> | null = null;

const parseJwtPayload = (token: string): JwtPayload | null => {
  try {
    const payload = token.split(".")[1];
    if (!payload) {
      return null;
    }

    return JSON.parse(atob(payload)) as JwtPayload;
  } catch {
    return null;
  }
};

const isTokenExpired = (token: string): boolean => {
  const payload = parseJwtPayload(token);
  if (!payload?.exp) {
    return false;
  }

  return payload.exp * 1000 <= Date.now();
};

const clearStoredAuth = () => {
  localStorage.removeItem("access_token");
  delete axios.defaults.headers.common["Authorization"];
};

interface User {
  id: string;
  email: string;
  name?: string;
  role: string;
  tenantId?: string | null;
}

interface AuthStore {
  user: User | null;
  isLoading: boolean;
  token: string | null;
  checkAuth: () => Promise<void>;
  login: (email: string, password: string, tenantId?: string) => Promise<User>;
  register: (data: any) => Promise<User>;
  logout: () => Promise<void>;
}

const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  isLoading: true,
  token: localStorage.getItem("access_token"),

  checkAuth: async () => {
    if (authCheckPromise) {
      return authCheckPromise;
    }

    const token = get().token;
    if (!token) {
      set({ isLoading: false });
      return;
    }

    if (isTokenExpired(token)) {
      clearStoredAuth();
      set({ user: null, token: null, isLoading: false });
      return;
    }

    authCheckPromise = (async () => {
      try {
        const { data } = await axios.get("/auth/me", {
          headers: { Authorization: `Bearer ${token}` },
        });
        set({ user: data.data.user, isLoading: false });
      } catch {
        clearStoredAuth();
        set({ user: null, token: null, isLoading: false });
      } finally {
        authCheckPromise = null;
      }
    })();

    return authCheckPromise;
  },

  login: async (email: string, password: string, tenantId?: string) => {
    const normalizedEmail = email.trim();
    const body: any = { email: normalizedEmail, password };
    if (tenantId) {
      body.tenantId = tenantId;
    }
    const { data } = await axios.post("/auth/login", body);
    const token = data.data.accessToken;
    const user = data.data.user;
    localStorage.setItem("access_token", token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    set({ user, token });
    return user;
  },

  register: async (formData: any) => {
    const { data } = await axios.post("/auth/register", formData);
    const token = data.data.accessToken;
    const user = data.data.user;
    localStorage.setItem("access_token", token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    set({ user, token });
    return user;
  },

  logout: async () => {
    await axios.post("/auth/logout");
    localStorage.removeItem("access_token");
    delete axios.defaults.headers.common["Authorization"];
    set({ user: null, token: null });
  },
}));

export const useAuth = () => {
  const store = useAuthStore();

  useEffect(() => {
    if (store.isLoading) {
      store.checkAuth();
    }
  }, []);

  return store;
};

export { useAuthStore };
